import { useState } from 'react'
import { Link, Skeleton } from '../../core/index.js'
import { useFacilityPages } from '../index.js'
import { FacilityAdministratorsSection, useFacilityAdministrators, useLoginInvitations } from '../../accounts/index.js'
import { FacilityIcon, WorkersIcon } from '../../core/index.js'
import type { FacilitySummary } from '../index.js'
import { ArrowRight, ChevronDown } from 'lucide-react'
import { useDebouncedValue } from '../../roster/index.js'
import { formatAddress } from '../components/facilityAddress.js'
import { Button } from '../../core/index.js'
import { LoadFailure, RefreshButton } from '../../core/index.js'

function WorkerCount({ count }: { count: number }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] text-ink-mid"
      title={count === 0 ? 'No workers imported for this facility yet' : `${count} on the roster`}
    >
      <WorkersIcon />
      {/* A facility with no roster yet is a real state - an operator who has
          not imported one needs to see that, not a blank space. */}
      {count === 0 ? 'No workers yet' : `${count} ${count === 1 ? 'worker' : 'workers'}`}
    </span>
  )
}

/** Where the facility is and which zone it keeps time in, or that neither has been set yet. */
function FacilityPlace({ facility }: { facility: FacilitySummary }) {
  const place = facility.address ? `${facility.address.city}, ${facility.address.region}` : 'No address yet'
  const zone = facility.timezone ? facility.timezone.replaceAll('_', ' ') : 'Time zone not set (reads as UTC)'
  return (
    <div className="text-[11px] text-ink-mid mt-0.5" title={facility.address ? formatAddress(facility.address) : undefined}>
      {place} &middot; {zone}
      {facility.phone && <> &middot; {facility.phone}</>}
    </div>
  )
}

/**
 * The facilities this person can see, in name order. They are loaded a
 * page at a time and found by the start of the name, both done by the API:
 * a client may have hundreds, and a list sorted or filtered here would
 * only ever cover the pages already loaded.
 */
export function FacilityList({
  total,
  clientId,
  canManageAdministrators,
}: {
  /** How many facilities there are in all, whatever the search shows. */
  total: number
  clientId: string | undefined
  canManageAdministrators: boolean
}) {
  const [searchText, setSearchText] = useState('')
  const search = useDebouncedValue(searchText)
  const pagesQuery = useFacilityPages(search)
  const facilities = pagesQuery.data?.pages.flatMap((page) => page.items) ?? []
  const isSearching = search.trim() !== ''
  const matching = pagesQuery.data?.pages[0]?.total ?? facilities.length
  const administratorsQuery = useFacilityAdministrators(clientId, canManageAdministrators)
  const invitationsQuery = useLoginInvitations(clientId, 'facility-administrators', canManageAdministrators)

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between gap-4 text-[10px] font-bold uppercase tracking-wide text-marine border-b border-marine/15 pb-2 mb-3">
        <span>
          Facilities ({total}){isSearching && !pagesQuery.isPlaceholderData && <span className="text-ink-mid normal-case tracking-normal font-normal"> &middot; {matching} {matching === 1 ? 'name starts' : 'names start'} with &ldquo;{search.trim()}&rdquo;</span>}
        </span>
        <span className="text-ink-mid normal-case tracking-normal font-normal">By name</span>
      </div>
      <div className="mb-3 flex items-center gap-2">
        <label htmlFor="facility-search" className="sr-only">
          Find a facility
        </label>
        <input
          id="facility-search"
          type="search"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Find a facility by the start of its name"
          autoComplete="off"
          className="w-full border border-marine/25 px-3 py-2 text-sm text-ink focus:outline-none focus:border-marine"
        />
        <RefreshButton
          className="shrink-0"
          onRefresh={() => Promise.all([pagesQuery.refetch(), ...(canManageAdministrators ? [administratorsQuery.refetch(), invitationsQuery.refetch()] : [])])}
          refreshing={pagesQuery.isFetching || administratorsQuery.isFetching || invitationsQuery.isFetching}
        />
      </div>
      {pagesQuery.isPending ? (
        <Skeleton lines={8} label="Loading facilities" />
      ) : pagesQuery.isError ? (
        <LoadFailure message="Failed to load facilities. Confirm the API is reachable." onRetry={() => void pagesQuery.refetch()} retrying={pagesQuery.isFetching} />
      ) : facilities.length === 0 ? (
        <div className="text-sm text-ink-mid">{isSearching ? 'No facility name starts with that.' : 'No facilities.'}</div>
      ) : (
      <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 ${pagesQuery.isPlaceholderData ? 'opacity-60' : ''}`}>
        {/* A tile per facility, four to a row (the user's call, 27 October 2026): the name, its roster and its place, then what an operator manages there. */}
        {facilities.map((facility) => (
          <div key={facility.facilityId} className="group flex min-w-0 flex-col rounded-lg border border-marine/15 bg-white transition-colors hover:border-marine/40">
            <Link to={`/facilities/${encodeURIComponent(facility.facilityId)}`} className="flex flex-1 flex-col gap-2 px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                {/* Marine rather than a tier colour: colour elsewhere in this app encodes
                    reliability, and a facility marker carries no such meaning. */}
                <FacilityIcon className="h-5 w-5 shrink-0 text-marine" />
                <ArrowRight aria-hidden="true" className="h-4 w-4 shrink-0 text-marine/40 transition-all group-hover:translate-x-0.5 group-hover:text-marine" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold leading-snug text-ink transition-colors group-hover:text-marine-dk">{facility.name}</div>
                <div className="mt-1">
                  <WorkerCount count={facility.workerCount} />
                </div>
                <FacilityPlace facility={facility} />
              </div>
            </Link>
            {canManageAdministrators && (
              <div className="flex flex-col gap-2 border-t border-marine/10 px-4 py-2">
                <Link to={`/facilities/${encodeURIComponent(facility.facilityId)}/config`} className="text-[11px] font-bold text-marine hover:text-marine-dk">
                  Edit name, time zone, address and phone
                </Link>
                <FacilityAdministratorsSection clientId={clientId} facility={facility} />
              </div>
            )}
          </div>
        ))}
      </div>
      )}
      {facilities.length > 0 && (
        <div className="mt-3 flex items-center gap-3">
          <span className="text-[11px] text-ink-mid">
            Showing {facilities.length.toLocaleString()} of {matching.toLocaleString()} {isSearching ? (matching === 1 ? 'matching facility' : 'matching facilities') : matching === 1 ? 'facility' : 'facilities'}
          </span>
          {pagesQuery.hasNextPage && (
            <Button icon={ChevronDown} variant="secondary" onClick={() => void pagesQuery.fetchNextPage()} disabled={pagesQuery.isFetchingNextPage}>
              {pagesQuery.isFetchingNextPage ? 'Loading…' : 'Show more'}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
